import { VoiceStrategy } from './VoiceStrategy';
import { ComposerSession } from '../ComposerSession';
import { RhythmEngine } from '../RhythmEngine';
import { MelodyGenerator } from '../MelodyGenerator';
import { StructureEngine } from '../StructureEngine';
import { midiToFreq } from '../../../utils/math';

export class SoloistVoice implements VoiceStrategy {
    constructor(
        private rhythmEngine: RhythmEngine,
        private melodyGenerator: MelodyGenerator,
        private structureEngine: StructureEngine
    ) { }

    getNextEvent(
        session: ComposerSession,
        scaleNotes: number[],
        baseVelocity: number,
        sectionVelMult: number,
        timbreData: { filter: number; release: number; reverb: number },
        tuning: number,
        dynamics?: { velocity: number; yPos: number },
        density?: 'sparse' | 'normal' | 'dense'
    ): {
        events: Array<{
            freq: number;
            delay: number;
            duration?: number;
            velocity?: number;
            timbre?: { filter: number; release: number; reverb: number };
        }>;
        stateUpdate?: {
            currentChordContext?: number[];
            currentBassNote?: number;
            lastMelodyNote?: number;
        };
    } {
        // Use current chords as guide (Maestro already updated context)
        const guideChordNotes = session.currentChordContext;
        const isStrongBeat = Math.random() < 0.2; // Less gravity for soloists

        // V13: HOOK ARCHITECT LOGIC (P0 Fix: Solunn Amnesia)
        // Enable Soloist to participate in catching the "Earworm"
        let hookMode: 'record' | 'replay' | 'none' = 'none';
        const currentSectionName = this.structureEngine.getCurrentSection();
        const prevSection = currentSectionName; // Snapshot before advance

        if (currentSectionName === 'Chorus') {
            if (session.hasRecordedHook) {
                hookMode = 'replay';
            } else {
                hookMode = 'record';
            }
        }

        // Phase 2: Rhythmic Capture (Simple generic fallback for soloist)
        const timeSinceLastNote = 0.5; // Todo: Track lastNoteTime in Soloist if we want precise rhythm capture

        const melodyResult = this.melodyGenerator.getNextMelodyNote(
            scaleNotes,
            0, // Anchor to scale root
            this.structureEngine.getCurrentSection(),
            session.currentMood,
            session.currentBassNote,
            guideChordNotes,
            isStrongBeat,
            session.lastMelodyNote, // Primary Voice Note for Counterpoint
            [], // Anticipation (none)
            0,  // Progress (none)
            'none', // Cadence (none)
            hookMode, // P0 FIX: Pass Hook Mode
            session.hookMotif, // P0 FIX: Pass Hook Buffer
            timeSinceLastNote
        );

        const melodyNote = melodyResult.note;
        const rhythmicOverride = melodyResult.rhythmicOverride;

        const velocity = this.rhythmEngine.humanizeVelocity(baseVelocity * sectionVelMult, true);

        // AUDIT FIX 3.0: Advance structure on Soloist touches too!
        // This ensures holding a chord (Maestro) while tapping (Soloist) still advances the song.
        // PHASE 2: Pass intensity
        const modEvent = this.structureEngine.advanceTouch(velocity);

        // P0 FIX: HOOK COMPLETION CHECK
        // If Soloist triggers the section change, we must also save the hook state!
        const newSection = this.structureEngine.getCurrentSection();
        if (prevSection !== newSection) {
            // Check if we just finished recording a hook
            if (prevSection === 'Chorus' && !session.hasRecordedHook && session.hookMotif.length > 0) {
                session.hasRecordedHook = true;
                if (process.env.NODE_ENV === 'development') {
                    console.log(`[SoloistVoice] Hook Recorded (Triggered by Soloist)! Length: ${session.hookMotif.length}`);
                }
            }
        }

        if (modEvent) {
            this.handleModulation(session, modEvent);
        }

        let duration: number | undefined = undefined;
        if (rhythmicOverride) {
            const grooveCtx = this.rhythmEngine.getGrooveContext();
            const bpm = grooveCtx.detectedBPM || 120;
            duration = rhythmicOverride * (60 / bpm);
        }

        return {
            events: [{
                freq: midiToFreq(melodyNote + session.currentKeyOffset, tuning),
                delay: 0,
                velocity: velocity,
                timbre: timbreData,
                duration: duration
            }],
            stateUpdate: {
                lastMelodyNote: melodyNote
            }
        };
    }

    private handleModulation(session: ComposerSession, event: 'ToRelativeMinor' | 'LiftWholeStep' | 'Reset') {
        if (event === 'Reset') {
            session.currentKeyOffset = 0;
        } else if (event === 'ToRelativeMinor') {
            if (session.scale === 'major' || session.scale === 'pentatonic_major') {
                session.currentKeyOffset = 7;
            } else {
                session.currentKeyOffset = 3;
            }
        } else if (event === 'LiftWholeStep') {
            session.currentKeyOffset = 2;
        }
    }
}
